/**
 * Overview — "can I spend money right now?"
 *
 * Leftover stays first. Everything under it is a section the person can hide or
 * reorder in Settings, the same blocks as Home on the phone.
 */

import { Link } from 'react-router-dom';
import {
  formatMoney,
  isCurrentPeriod,
  nextPeriod,
  periodLabel,
  previousPeriod,
  sumCents,
  type BudgetAlert,
  type BudgetItemBalance,
  type PaycheckPeriod,
} from '@alohamula/domain';
import { PageHeader } from '../components/AppShell';
import { Badge, Button, Section } from '../components/ui';
import { PinIcon } from '../components/icons';
import { useToast } from '../components/ui/ToastProvider';
import { useUpdateBudgetItem } from '../api/queries';
import type { Budget } from '../budget/useBudget';
import { readHomeLayout, visibleHomeOrder } from '../homeLayout';
import './pages.css';

interface OverviewPageProps {
  budget: Budget;
  onSelectPeriod: (period: PaycheckPeriod) => void;
  isRefreshing: boolean;
  onRefresh: () => void;
}

export function OverviewPage({
  budget,
  onSelectPeriod,
  isRefreshing,
  onRefresh,
}: OverviewPageProps) {
  const { period, leftThisPeriod, spentThisPeriod, snapshot, pending, alerts } = budget;
  const isCurrent = isCurrentPeriod(period, new Date());
  const overspent = leftThisPeriod < 0;
  const order = visibleHomeOrder(readHomeLayout());
  const envelopeRemaining = remainingOf(budget.envelopes);
  const billRemaining = remainingOf(budget.bills);
  const overspentItems = [...budget.envelopes, ...budget.bills]
    .filter((balance) => balance.isOverBudget)
    .sort((a, b) => a.remaining - b.remaining);
  const pinned = [...budget.envelopes, ...budget.bills]
    .filter((balance) => balance.item.isPinned)
    .sort((a, b) => a.item.name.localeCompare(b.item.name));
  const hasNeeds =
    Math.abs(budget.unallocatedPaycheck) >= 0.01 ||
    alerts.length > 0 ||
    pending.holdCount > 0 ||
    budget.uncategorized.length > 0 ||
    overspentItems.length > 0;

  return (
    <>
      <PageHeader
        title="Overview"
        period={period}
        action={
          <div className="period">
            <Button
              size="sm"
              aria-label="Previous paycheck"
              onClick={() => onSelectPeriod(previousPeriod(period))}
            >
              ←
            </Button>
            <span className="period__label">{isCurrent ? 'This paycheck' : periodLabel(period)}</span>
            <Button
              size="sm"
              aria-label="Next paycheck"
              disabled={isCurrent}
              onClick={() => onSelectPeriod(nextPeriod(period))}
            >
              →
            </Button>
            <Button size="sm" loading={isRefreshing} onClick={onRefresh}>
              Refresh
            </Button>
          </div>
        }
      />

      <div className="card headline">
        <div className="headline__figure">
          <span className="headline__label">
            {overspent ? 'Over this paycheck' : 'Left to spend'}
          </span>
          <span className={`headline__value${overspent ? ' headline__value--over' : ''}`}>
            {formatMoney(Math.abs(leftThisPeriod))}
          </span>
          <span className="headline__note">
            {formatMoney(spentThisPeriod)} spent of{' '}
            {formatMoney(snapshot.paycheckSettings.paycheckAmount)}
            {' · '}
            {periodLabel(period)}
          </span>
        </div>
      </div>

      {order.map((section) => {
        if (section === 'plan') {
          return (
            <Section title="Envelopes & Bills" key={section}>
              <div className="card pin-list">
                <Link to="/plan" className="pin-row">
                  <span className="pin-row__main">
                    <span className="pin-row__name">Envelopes</span>
                    <span className="pin-row__spent">Left to spend</span>
                  </span>
                  <span className="pin-row__value">{formatMoney(envelopeRemaining)}</span>
                </Link>
                <Link to="/plan" className="pin-row">
                  <span className="pin-row__main">
                    <span className="pin-row__name">Bills</span>
                    <span className="pin-row__spent">Left for bills</span>
                  </span>
                  <span className="pin-row__value">{formatMoney(billRemaining)}</span>
                </Link>
              </div>
            </Section>
          );
        }
        if (section === 'needs' && hasNeeds) {
          return (
            <NeedsSection
              key={section}
              budget={budget}
              pendingHolds={pending.holdsTotal}
              pendingCount={pending.holdCount}
              overspent={overspentItems}
            />
          );
        }
        if (section === 'pinned' && pinned.length > 0) {
          return <PinnedSection key={section} pinned={pinned} />;
        }
        if (section === 'paycheck') {
          return (
            <Section title="Paycheck" key={section}>
              <div className="card pin-list">
                <div className="pin-row">
                  <span className="pin-row__name">Amount</span>
                  <span className="pin-row__value pin-row__value--ink">
                    {formatMoney(snapshot.paycheckSettings.paycheckAmount)}
                  </span>
                </div>
                <div className="pin-row">
                  <span className="pin-row__name">Envelopes</span>
                  <span className="pin-row__value">{formatMoney(budget.envelopesFunded)}</span>
                </div>
                <div className="pin-row">
                  <span className="pin-row__name">Bills</span>
                  <span className="pin-row__value">{formatMoney(budget.billsFunded)}</span>
                </div>
              </div>
            </Section>
          );
        }
        return null;
      })}
    </>
  );
}

function remainingOf(balances: BudgetItemBalance[]): number {
  return sumCents(
    balances.filter((balance) => !balance.item.parentId).map((balance) => balance.remaining)
  );
}

function NeedsSection({
  budget,
  pendingHolds,
  pendingCount,
  overspent,
}: {
  budget: Budget;
  pendingHolds: number;
  pendingCount: number;
  overspent: BudgetItemBalance[];
}) {
  const gap = budget.unallocatedPaycheck;
  const hasGap = Math.abs(gap) >= 0.01;
  return (
    <Section
      title="Needs you"
      meta={`${
        (hasGap ? 1 : 0) +
        budget.alerts.length +
        (pendingCount > 0 ? 1 : 0) +
        (budget.uncategorized.length > 0 ? 1 : 0) +
        Math.min(overspent.length, 4)
      }`}
    >
      <div className="card alerts">
        {hasGap && (
          <Link to="/settings" className="alert" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span
              className={`alert__bar ${gap < 0 ? 'alert__bar--critical' : 'alert__bar--warning'}`}
              aria-hidden="true"
            />
            <div className="alert__text">
              <p className="alert__title">Doesn&apos;t add up</p>
              <p className="alert__message">
                {gap > 0
                  ? `${formatMoney(gap)} of this paycheck is not assigned to any envelope or bill.`
                  : `Envelopes and bills need ${formatMoney(Math.abs(gap))} more than this paycheck.`}
              </p>
            </div>
          </Link>
        )}
        {budget.uncategorized.length > 0 && (
          <Link to="/sort" className="alert" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span className="alert__bar alert__bar--info" aria-hidden="true" />
            <div className="alert__text">
              <p className="alert__title">Needs sorting</p>
              <p className="alert__message">
                {budget.uncategorized.length === 1
                  ? '1 charge still needs a category.'
                  : `${budget.uncategorized.length} charges still need a category.`}
              </p>
            </div>
          </Link>
        )}
        {pendingCount > 0 && (
          <div className="alert">
            <span className="alert__bar alert__bar--warning" aria-hidden="true" />
            <div className="alert__text">
              <p className="alert__title">Pending holds</p>
              <p className="alert__message">
                {formatMoney(Math.abs(pendingHolds))} held · {pendingCount}{' '}
                {pendingCount === 1 ? 'charge' : 'charges'}
              </p>
            </div>
          </div>
        )}
        {budget.alerts.map((alert) => (
          <AlertRow key={alert.key} alert={alert} />
        ))}
        {overspent.slice(0, 4).map((balance) => (
          <Link
            key={balance.item.id}
            to="/plan"
            className="alert"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <span className="alert__bar alert__bar--critical" aria-hidden="true" />
            <div className="alert__text">
              <p className="alert__title">{balance.item.name}</p>
              <p className="alert__message">Overspent</p>
            </div>
            <span className="pin-row__value pin-row__value--over">{formatMoney(balance.remaining)}</span>
          </Link>
        ))}
      </div>
    </Section>
  );
}

function PinnedSection({ pinned }: { pinned: BudgetItemBalance[] }) {
  const pin = useUpdateBudgetItem();
  const toast = useToast();
  return (
    <Section
      title="Pinned"
      action={
        <Link to="/plan" className="section__meta">
          Envelopes
        </Link>
      }
    >
      <div className="card pin-list">
        {pinned.map((balance) => (
          <div className="pin-row" key={balance.item.id}>
            <Link to="/plan" className="pin-row__main">
              <span className="pin-row__name">{balance.item.name}</span>
              <span className="pin-row__spent">
                {formatMoney(balance.spent)} of {formatMoney(balance.available)}
              </span>
            </Link>
            <span className={`pin-row__value${balance.isOverBudget ? ' pin-row__value--over' : ''}`}>
              {formatMoney(balance.remaining)}
            </span>
            <button
              type="button"
              className="pin-row__unpin"
              aria-label={`Unpin ${balance.item.name}`}
              onClick={async () => {
                try {
                  await pin.mutateAsync({
                    id: balance.item.id,
                    input: {
                      isPinned: false,
                      expectedVersion: balance.item.version,
                    },
                  });
                  toast.show(`Unpinned ${balance.item.name}`);
                } catch (error) {
                  toast.show(error instanceof Error ? error.message : 'Could not update that pin.');
                }
              }}
            >
              <PinIcon width={16} height={16} />
            </button>
          </div>
        ))}
      </div>
    </Section>
  );
}

const ALERT_BADGE = {
  critical: { tone: 'danger', label: 'Over' },
  warning: { tone: 'warning', label: 'Watch' },
  info: { tone: 'action', label: 'To do' },
} as const;

function AlertRow({ alert }: { alert: BudgetAlert }) {
  const badge = ALERT_BADGE[alert.severity];
  return (
    <div className="alert">
      <span className={`alert__bar alert__bar--${alert.severity}`} aria-hidden="true" />
      <div className="alert__text">
        <p className="alert__title">{alert.title}</p>
        <p className="alert__message">{alert.message}</p>
      </div>
      <Badge tone={badge.tone}>{badge.label}</Badge>
    </div>
  );
}
